import authStore from '@/stores/authStore'
import { ApiResponse } from '@/types'
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

class ApiService {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      timeout: 30000,
      // 不全局设置 Content-Type，让每个请求自动决定
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // 请求拦截器
    this.instance.interceptors.request.use(
      config => {
        // 允许大小写任意的 skipAuth / SkipAuth
        const skipKey = Object.keys(config.headers || {}).find(k => k.toLowerCase() === 'skipauth')
        if (skipKey) {
          delete config.headers[skipKey]
        } else {
          const token = authStore.token
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        }
        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse<any>) => {
        // 检查业务返回的 code 或 info，判断 token 是否过期或无权限
        if (
          response.data &&
          (response.data.message?.includes('Token expired') ||
            response.data.info?.includes('Token expired') ||
            response.data.code === 'A400401' ||
            response.data.info?.includes('无权限'))
        ) {
          // Token过期或无权限，清除存储并跳转到登录页
          authStore.logout()
          window.location.href = '/login'
          return Promise.reject(new Error('Token expired or no permission'))
        }

        // 检查登录失败的情况 - 登录接口返回code字段表示失败
        const isLoginRequest = response.config.url?.includes('/login')
        if (isLoginRequest && response.data && response.data.code && response.data.code !== '200') {
          return Promise.reject(
            new Error(response.data.info || response.data.message || '登录失败')
          )
        }

        // 移除业务错误拦截，让调用方自己处理业务错误
        // 只拦截网络错误和认证错误

        return response
      },
      (error: any) => {
        if (
          error.response?.status === 401 ||
          error.response?.data?.message?.includes('Token expired') ||
          error.response?.data?.info?.includes('Token expired') ||
          error.response?.data?.code === 'A400401' ||
          error.response?.data?.info?.includes('无权限')
        ) {
          // Token过期或无权限，清除存储并跳转到登录页
          authStore.logout()
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    )
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.get<ApiResponse<T>>(url, config)
    return response.data
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.post<ApiResponse<T>>(url, data, config)
    return response.data
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.put<ApiResponse<T>>(url, data, config)
    return response.data
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.delete<ApiResponse<T>>(url, config)
    return response.data
  }

  // 文件上传
  async upload<T = any>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.instance.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })

    return response.data
  }

  async login(username: string, password: string) {
    const response = await this.instance.post(
      '/OTA-AuthHandler/login',
      JSON.stringify({ username, password }),
      {
        headers: { 'Content-Type': 'text/plain' },
      }
    )
    return response.data
  }

  // 文件下载
  async download(url: string, params?: any): Promise<Blob> {
    const response = await this.instance.get(url, {
      params,
      responseType: 'blob',
    })
    return response.data
  }
}

const apiService = new ApiService()
export default apiService 